import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CircularProgress from '@mui/material/CircularProgress';
import { datadogLogs } from '@datadog/browser-logs';
import { TextSpan } from '../../../../Atoms/Text';
import { useSnackbar } from '../../../../../Context/SnackbarContext';
import { DEFAULT_ADVANCE_BUTTON_STYLE } from '../../../../../utils/styleHelpers';
import { WorkflowEvent, WorkflowType } from '../../../../../store/WorkflowEvent';
import { getFormattedDate } from '../../../../../utils/helpers';
import { CallResult } from '../../../../../Api/APIService';
import { useAuthenticatedSocket, LIVE_UPDATES_UNAVAILABLE } from '../../../../../Api/useAuthenticatedSocket';
import { addError, clearError } from '../../../../../store/Error';

interface CalculationWorkflowProps {
  type: WorkflowType;
  getCurrentWorkflow: () => Promise<CallResult<WorkflowEvent>>;
  startWorkflow: () => Promise<CallResult<void>>;
}

// One domain per instance -- a shared key would let one workflow's recovery clear another's outage.
const LIVE_UPDATES_DOMAIN = {
  [WorkflowType.RUN_TOTALS]: 'live-updates-run-totals',
  [WorkflowType.LINK_ALLOCATIONS]: 'live-updates-link-allocations'
} as const;

const workflowData: {
  [key in WorkflowType]: { title: string; description: string; stages: { name: string; label: string }[] };
} = {
  [WorkflowType.RUN_TOTALS]: {
    title: 'Run Totals',
    description: 'Force recalculation of all Run Totals',
    stages: [
      { name: 'RpmAccountRunTotals', label: 'Rpm Account Run Totals' },
      { name: 'AssetRunTotals', label: 'Asset Run Totals' },
      { name: 'ProvisionalAssetRunTotals', label: 'Provisional Asset Run Totals' },
      { name: 'TicketRunTotals', label: 'Ticket Run Totals' },
      { name: 'CustomerSupplierRunTotals', label: 'Customer Supplier Run Totals' },
      { name: 'CustomerRunTotals', label: 'Customer Run Totals' }
    ]
  },
  [WorkflowType.LINK_ALLOCATIONS]: {
    title: 'Link Allocation',
    description: 'Force recalculation of all Link Allocation values',
    stages: [
      { name: 'ProcessingRunIds', label: 'Finding Processing Runs' },
      { name: 'LinkAllocations', label: 'Link Allocations' },
      { name: 'Attributions', label: 'Attributions' },
      { name: 'CustomerSupplierAttributions', label: 'Customer Supplier Attributions' },
      { name: 'CustomerAttributions', label: 'Customer Attributions' }
    ]
  }
};

// A socket message is a boundary: validate before trusting completed_stages.includes below.
const isWorkflowEvent = (data: unknown): data is WorkflowEvent =>
  typeof data === 'object' &&
  data !== null &&
  typeof (data as WorkflowEvent).status === 'string' &&
  Array.isArray((data as WorkflowEvent).completed_stages) &&
  typeof (data as WorkflowEvent).workflow_type === 'string';

export const CalculationWorkflow = ({ type, getCurrentWorkflow, startWorkflow }: CalculationWorkflowProps) => {
  const { setSnack } = useSnackbar();
  const [started, setStarted] = useState<boolean>(false);
  const [currentWorkflow, setCurrentWorkflow] = useState<WorkflowEvent | null>(null);
  const [lastStarted, setLastStarted] = useState<string | undefined>();

  const applyWorkflowEvent = useCallback((data: WorkflowEvent | null) => {
    if (data) setLastStarted(getFormattedDate(data.createdAt, 'LLL'));
    if (data?.status === 'running') {
      setCurrentWorkflow(data);
    } else {
      setCurrentWorkflow(null);
      setStarted(false);
    }
  }, []);

  const fetchWorkflow = useCallback(async () => {
    const { data, error } = await getCurrentWorkflow();
    // A CallResult never throws -- the old try/catch here was dead code.
    if (error) {
      setSnack({ message: 'Could not read the current workflow status.', type: 'error', open: true });
      setCurrentWorkflow(null);
      return;
    }
    applyWorkflowEvent(data);
  }, [getCurrentWorkflow, setSnack, applyWorkflowEvent]);

  // Kept mount-once via a ref: getCurrentWorkflow's prop type doesn't guarantee a stable identity,
  // so keying the effect on fetchWorkflow itself would risk a fetch loop for a future inline caller.
  const fetchWorkflowRef = useRef(fetchWorkflow);
  useEffect(() => {
    fetchWorkflowRef.current = fetchWorkflow;
  });
  useEffect(() => {
    fetchWorkflowRef.current();
  }, []);

  const events = useMemo(
    () => ({
      workflowEvent: (data: WorkflowEvent) => {
        if (!isWorkflowEvent(data)) {
          datadogLogs.logger.warn('Discarding a workflowEvent payload that failed the shape check', { data });
          return;
        }
        if (data.workflow_type === type) applyWorkflowEvent(data);
      }
    }),
    [type, applyWorkflowEvent]
  );

  // Skips only the very first signal (mount already fetched) -- every later one resyncs, even
  // a reconnect that never crossed the 4-attempt unavailable threshold.
  const hasHandledAvailabilityRef = useRef(false);

  const handleAvailability = useCallback(
    (available: boolean) => {
      const domain = LIVE_UPDATES_DOMAIN[type];
      if (available) {
        clearError(domain);
        if (hasHandledAvailabilityRef.current) fetchWorkflow();
      } else {
        addError(domain, LIVE_UPDATES_UNAVAILABLE);
      }
      hasHandledAvailabilityRef.current = true;
    },
    [fetchWorkflow, type]
  );

  useAuthenticatedSocket(events, handleAvailability);

  // Otherwise navigating away mid-outage leaves this domain's error in the store indefinitely --
  // it can only self-clear through handleAvailability above, which needs the component mounted.
  useEffect(() => {
    return () => clearError(LIVE_UPDATES_DOMAIN[type]);
  }, [type]);

  const startRunTotals = async () => {
    const { error } = await startWorkflow();
    if (error) {
      setSnack({ message: `Could not start ${workflowData[type].title}.`, type: 'error', open: true });
      return;
    }
    setStarted(true);
    setSnack({ message: `${workflowData[type].title} started`, type: 'success', open: true });
  };

  const getIconForStage = (stage: string) => {
    if (currentWorkflow?.completed_stages.includes(stage)) {
      return <CheckIcon sx={{ fontSize: 14, color: 'indigo' }} />;
    } else {
      return (
        <CircularProgress
          size={12}
          color="primary"
        />
      );
    }
  };

  const dataCy = `calculation-button-${type.toLowerCase().replace('_', '-')}`;

  return (
    <Fragment>
      <div className="flex p-3">
        <TextSpan className="flex items-center space-x-2">
          <button
            data-cy={dataCy}
            disabled={started || currentWorkflow !== null}
            className={`${DEFAULT_ADVANCE_BUTTON_STYLE} mr-10`}
            onClick={startRunTotals}>
            Calculate {workflowData[type].title}
          </button>
          <div className="flex flex-col">
            <span>{workflowData[type].description}.</span>
            {lastStarted && <span className="text-sm font-bold">{`Last Calculation: ${lastStarted}.`}</span>}
          </div>
        </TextSpan>
      </div>

      <Fragment>
        {currentWorkflow !== null && (
          <div className="flex p-3">
            <TextSpan className="flex flex-col space-y-2">
              <div className="flex flex-col space-y-2 p-3">
                {workflowData[type].stages.map((stage) => (
                  <div
                    key={stage.name}
                    className="flex items-center space-x-2">
                    {getIconForStage(stage.name)}
                    <Typography variant="body2">{stage.label}</Typography>
                  </div>
                ))}
              </div>
            </TextSpan>
          </div>
        )}
      </Fragment>
    </Fragment>
  );
};
